import React, { useState } from 'react';
import { User, Lock, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { updateCurrentUser } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const Settings = () => {
  const { user, refreshUser } = useAuth();

  const [name, setName] = useState(user?.name || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      await updateCurrentUser({ name: name.trim() });
      if (refreshUser) await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8 || !/\d/.test(newPassword) || !/[a-zA-Z]/.test(newPassword)) {
      toast.error('Password must be at least 8 characters and include a letter and a number');
      return;
    }
    setSavingPassword(true);
    try {
      await updateCurrentUser({ currentPassword, password: newPassword });
      toast.success('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold">Settings</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">Manage your account</p>
      </div>

      {/* Profile section */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5 mb-4">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-[#A1A1AA]" />
          Profile
        </h2>
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-[#A1A1AA]">Display Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#09090B] border-[#27272A] mt-1"
              placeholder="Your name"
            />
          </div>
          <div>
            <Label className="text-sm text-[#A1A1AA]">Email</Label>
            <Input
              value={user?.email || ''}
              disabled
              className="bg-[#09090B] border-[#27272A] mt-1 text-[#52525B]"
            />
            <p className="text-xs text-[#52525B] mt-1">Email cannot be changed</p>
          </div>
          <div>
            <Label className="text-sm text-[#A1A1AA]">Role</Label>
            <Input
              value={user?.role || ''}
              disabled
              className="bg-[#09090B] border-[#27272A] mt-1 text-[#52525B] capitalize"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            onClick={handleSaveProfile}
            disabled={savingProfile || name.trim() === (user?.name || '')}
            className="btn-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>

      {/* Password section */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#A1A1AA]" />
          Change Password
        </h2>
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-[#A1A1AA]">Current Password</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-[#09090B] border-[#27272A] mt-1"
              placeholder="••••••••"
            />
          </div>
          <div>
            <Label className="text-sm text-[#A1A1AA]">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-[#09090B] border-[#27272A] mt-1"
              placeholder="8+ characters, letters and numbers"
            />
          </div>
          <div>
            <Label className="text-sm text-[#A1A1AA]">Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-[#09090B] border-[#27272A] mt-1"
              placeholder="••••••••"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            onClick={handleChangePassword}
            disabled={savingPassword}
            className="btn-primary"
          >
            <Lock className="w-4 h-4 mr-2" />
            {savingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
